/**
 * The Map Review map's Feature-point banding.
 *
 * A Feature point's band is read from its EFFECTIVE control-window end — the
 * server's `window_end` on the map payload, which is the active manual plan
 * where it governs and the latest residual expiry otherwise (the same value
 * platform/residual.go WindowEnd derives and ControlState judges). Banding it
 * against the clock splits the two states the server reports into three:
 *
 *   expired    the window end is past
 *   due_soon   the window end is at or after now but within the horizon
 *   controlled the window end is beyond the horizon
 *   unknown    nothing contributes a window (window_end is null)
 *
 * `expired`, `controlled` and `unknown` agree with the server's control_state;
 * `due_soon` is purely a client view of the same dates, which is what lets the
 * horizon be a slider that re-bands the already-loaded points with no refetch.
 * The module is pure so the banding and the legend counts it drives are
 * unit-testable without a browser.
 */

/** One Feature point's control band, as the map and the legend render it. */
export type ControlBand = "expired" | "due_soon" | "controlled" | "unknown";

/** The bands in legend order: the states needing attention first. */
export const CONTROL_BANDS: readonly ControlBand[] = [
	"expired",
	"due_soon",
	"controlled",
	"unknown",
];

/** The due-soon horizon in days: the operator's default, and the slider's range. */
export const DUE_SOON_DEFAULT_DAYS = 5;
export const DUE_SOON_MIN_DAYS = 1;
export const DUE_SOON_MAX_DAYS = 30;

/**
 * Band colours (Okabe–Ito where the band already had a colour). `expired` is a
 * maroon purple, deliberately far in hue from `due_soon`'s orange and
 * `controlled`'s bluish green so the three never flatten together under a
 * colour-vision deficiency; `unknown` reuses the neutral dim grey because it is
 * the absence of a window rather than a verdict. Colour is the only cue the map
 * gives a band — every point is drawn the same size and shape — so the palette
 * is chosen to stay separable under colour-vision deficiency.
 */
export const BAND_COLOR: Record<ControlBand, string> = {
	expired: "#7B2D6B",
	due_soon: "#E69F00",
	controlled: "#009E73",
	unknown: "#9ca3af",
};

/** The bands' row labels, shared by the control pane so the map's rows and the
 * Feature ledger's control column read as one vocabulary. */
export const BAND_LABEL: Record<ControlBand, string> = {
	expired: "Past control",
	due_soon: "Due soon",
	controlled: "Controlled",
	unknown: "Control unknown",
};

/** Milliseconds in a day, shared with the MapLibre band expressions that band a
 * tile's `window_end_ms` against the live horizon. */
export const DAY_MS = 86_400_000;

/**
 * controlBand places a Feature point in a band from its effective window end
 * (`window_end`, an RFC3339 instant or null), the clock `now` in epoch
 * milliseconds, and the due-soon horizon in days. A missing or unparseable
 * window end is `unknown`; an end strictly before now is `expired`; an end
 * within the horizon is `due_soon`; anything later is `controlled`.
 */
export function controlBand(
	windowEnd: string | null | undefined,
	now: number,
	horizonDays: number,
): ControlBand {
	if (!windowEnd) return "unknown";
	const end = Date.parse(windowEnd);
	if (!Number.isFinite(end)) return "unknown";
	if (end < now) return "expired";
	if (end <= now + horizonDays * DAY_MS) return "due_soon";
	return "controlled";
}
